import React, { useEffect } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { bindActionCreators, compose } from 'redux'
import { connect } from 'react-redux'
import Select from '../form/Select'
import { formatOptions } from '../../data'
import { desktop, mobile, VIEWPORT_DESKTOP } from '../../styles/media'
import formats from '../../data/formats'
import translate from '../../helper/translate'
import { showToast } from '../../state/actions/app'
import { getSeasonName, seasonsRemoveNumber } from '../../helper/card'
import { loadSeasons } from '../../state/actionCreators/app'

const FormatSeasonSelect = ({ deckbuilder, disabled, disabledHint, format, formatOnChange, loadSeasons, season, seasons, seasonOnChange, selectColor, showToast, t, viewport }) => {
	const seasonsListFormat = formats[format].parent || format

	useEffect(() => {
		if (!disabled && !season && seasons && seasons[format]) {
			seasonOnChange({
				...seasons[format][0].options[0],
				number: undefined
			})
		}
	}, [format, seasons])

	const showDisabledHint = () => {
		showToast('info', disabledHint)
	}

	useEffect(() => {
		loadSeasons(seasonsListFormat)
	}, [seasonsListFormat])

	return (
		<StyledFormatSeasonSelect
			disabled={disabled}
			onClick={disabled && disabledHint ? showDisabledHint : undefined}
			selectColor={selectColor}
			deckbuilder={deckbuilder}
		>
			<Select
				data={formatOptions()}
				value={format}
				onChange={(format) => {
					formatOnChange(Number(format))
				}}
				placeholder={t('general.game')}
				searchable={viewport === VIEWPORT_DESKTOP}
				disabled={disabled}
			/>
			{formats[format].hasSeasons && (
				<Select
					data={seasons && seasons[seasonsListFormat] ? seasonsRemoveNumber(seasons[seasonsListFormat]) : null}
					clearable
					value={season}
					onChange={seasonOnChange}
					valueUseObject
					placeholder={!seasons ? t('general.loading') : t('general.format')}
					disabled={disabled}
				/>
			)}
		</StyledFormatSeasonSelect>
	)
}

FormatSeasonSelect.propTypes = {
	deckbuilder: PropTypes.bool,
	disabled: PropTypes.bool,
	disabledHint: PropTypes.string,
	format: PropTypes.number.isRequired,
	formatOnChange: PropTypes.func.isRequired,
	loadSeasons: PropTypes.func.isRequired,
	season: PropTypes.object,
	seasons: PropTypes.object,
	seasonOnChange: PropTypes.func.isRequired,
	selectColor: PropTypes.string,
	showToast: PropTypes.func.isRequired,
	t: PropTypes.func.isRequired,
	viewport: PropTypes.string.isRequired
}

FormatSeasonSelect.defaultProps = {
	deckbuilder: false,
	disabled: false,
	disabledHint: null,
	season: null,
	seasons: null,
	selectColor: null
}

export const StyledFormatSeasonSelect = styled.div`
	display: flex;

	.react-select__control {
		background: transparent !important;
		opacity: 1 !important;
		width: auto !important;

		min-height: 27px;

		& + & {
			margin-left: 0.5rem;
		}

		.react-select__dropdown-indicator,
		.react-select__clear-indicator {
			color: #AAA;
			padding: 4px;

			&:hover {
				color: ${props => props.theme.backgroundConcrete};
			}

			svg {
				height: 16px;
				width: 16px;
			}
		}

		.react-select__value-container {
			padding: 0 calc(0.25rem - 1px);

			> :last-child {
				margin: 0;
				width: 0;
			}
		}

		.react-select__input {
			color: #FFF;
			margin: 0;
			padding: 0;
		}

		.react-select__single-value {
			position: relative;
			top: 8px;
			margin: 0;
			max-width: none;
			font-size: 0.9rem;

			${props => props.textWhite && `
				color: #FFF;
			`}

			span {
				max-width: none;
			}
		}

		.react-select__multi-value__remove {
			color: #555;
		}

		.react-select__indicator-separator, .react-select__dropdown-indicator {
			display: none;
		}

		&.react-select__control--is-focused {
			border-color: ${props => props.selectColor || props.theme.primary};
			box-shadow: 0 0 0 1px ${props => props.selectColor || props.theme.primary};
		}
	}

	.react-select__menu {
		width: 170px;
		max-width: none !important;
	}

	> :nth-child(2) {
		${props => !props.disabled && 'min-width: 150px;'}

		.react-select__single-value {
			color: #DDD;
		}
	}

	${desktop`
		.react-select__control {
			&:not(.react-select__control--is-focused) {
				border-color: transparent !important;
			}

			&:not(.react-select__control--is-focused):hover {
				border-color: rgba(255, 255, 255, 0.15) !important;
			}
		}
	`}

	${mobile`
		${props => props.deckbuilder ? `
			.react-select__control {
				&:not(.react-select__control--is-focused) {
					border-color: transparent !important;
				}
			}
		` : `
			.react-select__control {
				font-size: 0.8rem;
				min-height: 29px;
				min-width: 100px;

				&:not(.react-select__control--is-focused) {
					border-color: ${props.theme.backgroundLight} !important;
					box-shadow: 0 0 0 1px ${props.theme.backgroundLight};
				}
			}
		`}
	`}
`

const enhance = compose(
	connect(state => ({
		seasons: state.app.seasons,
		viewport: state.app.viewport
	}), dispatch => bindActionCreators({
		loadSeasons,
		showToast
	}, dispatch)),
	translate('general')
)

export default enhance(FormatSeasonSelect)
